import {
  createInfiniteSearchSelect,
  FormSelectProps,
} from '../../../common/form';
import {
  DataObjectFormDataType,
  DataObjectOutputType,
  DataObjectType,
} from '../../types';
import React, { useCallback, useMemo } from 'react';
import {
  FieldPath,
  FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import {
  AlgorithmSourceOutSourceSelectOption,
  useInfiniteSearchAlgorithmSourceObjectsQuery,
} from './query/useInfiniteSearchAlgorithmSourceObjectsQuery';
import { DefaultOptionType } from 'rc-select/lib/Select';

export interface AlgorithmSourceOutSourceSelectProps<
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
  Value extends FormData[Name] = FormData[Name],
> extends FormSelectProps<FormData, Name, Value> {
  index: number;
  defaultDataObjectValues: DataObjectFormDataType;
}

const AlgorithmSourceOutSourceInfiniteSelect = createInfiniteSearchSelect(
  useInfiniteSearchAlgorithmSourceObjectsQuery,
);

export const AlgorithmSourceOutSourceSelect = <
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
  Value extends FormData[Name] = FormData[Name],
>(
  props: AlgorithmSourceOutSourceSelectProps<FormData, Name, Value>,
) => {
  const { name, index, defaultDataObjectValues, ...rest } = props;
  const { getValues, setValue } = useFormContext();
  const defaultSource = getValues(`sources.${index}`);
  const defaultOption = useMemo(() => {
    if (!defaultSource.sourceId) {
      return undefined;
    }
    return {
      code: defaultSource.sourceCode,
      name: defaultSource.sourceName,
      domainId: defaultSource.domainId,
      dataObjectType: defaultSource.dataObjectDescription?.dataObjectType,
      outputTypeCode: defaultSource.dataObjectDescription?.outputTypeCode,
      dataObjectDescription: defaultSource.dataObjectDescription,
      value: defaultSource.sourceId,
      label:
        defaultSource.sourceId === -1
          ? ''
          : `${defaultSource.sourceCode} - ${defaultSource.sourceName}`,
      dimensions: defaultSource.dimensions,
    } as AlgorithmSourceOutSourceSelectOption;
  }, [defaultSource]);

  const [domainId, type] = useWatch({
    name: [`sources.${index}.domainId`, `sources.${index}.sourceType`],
  });
  const dataObjectType = useMemo(
    () =>
      !type
        ? undefined
        : type === 'IN'
          ? DataObjectType.IN
          : DataObjectType.OUT,
    [type],
  );
  const outputTypeCodes = useMemo(
    () =>
      type === 'OUTPUT'
        ? [DataObjectOutputType.OUTPUT]
        : type === 'INTERMEDIATE'
          ? [DataObjectOutputType.INTERMEDIATE]
          : type === 'REPORT'
            ? [DataObjectOutputType.REPORT]
            : [],
    [type],
  );

  const includeDataObjectIds = useMemo(() => {
    if (
      (defaultDataObjectValues.outputTypeCode === DataObjectOutputType.REPORT &&
        defaultDataObjectValues.dataObjectId &&
        !type) ||
      type === 'REPORT'
    ) {
      return [defaultDataObjectValues.dataObjectId as number];
    }
    return [];
  }, [type, defaultDataObjectValues]);

  const outputDataObjectId = useMemo(() => {
    if (
      defaultDataObjectValues.outputTypeCode ===
      DataObjectOutputType.INTERMEDIATE
    ) {
      return defaultDataObjectValues.outputDataObjectId;
    }
    return defaultDataObjectValues.dataObjectId;
  }, [defaultDataObjectValues]);

  const searchOptions = useMemo(
    () => ({
      includeDataObjectIds,
      dataObjectType,
      outputTypeCodes,
      outputDataObjectId,
      domainId,
    }),
    [
      includeDataObjectIds,
      dataObjectType,
      outputTypeCodes,
      outputDataObjectId,
      domainId,
    ],
  );

  const handleChange = useCallback(
    (
      value: string | number | null,
      option: DefaultOptionType | DefaultOptionType[],
    ) => {
      const selected = Array.isArray(option) ? option[0] : option;
      const opts = { shouldDirty: true };
      if (selected) {
        const type = selected.outputTypeCode
          ? selected.outputTypeCode.toString()
          : selected.dataObjectType
            ? selected.dataObjectType.toString()
            : '';
        setValue(`sources.${index}.sourceType`, type, opts);
        setValue(`sources.${index}.sourceCode`, selected.code, opts);
        setValue(`sources.${index}.sourceName`, selected.name, opts);
        setValue(`sources.${index}.dimensions`, selected.dimensions, opts);
        setValue(`sources.${index}.domainId`, selected.domainId, opts);
      }
    },
    [index, setValue],
  );

  return (
    <AlgorithmSourceOutSourceInfiniteSelect
      {...rest}
      defaultOption={defaultOption}
      styles={{
        popup: { root: { width: '350px' } },
      }}
      searchOptions={searchOptions}
      name={name}
      onChange={handleChange}
    />
  );
};
